import { Box, Button, Flex, Stack } from '@mantine/core';
import { IconAsterisk, IconChevronLeft, IconCodeAsterix, IconEye, IconRefresh } from '@tabler/icons-react';
import Loader from 'components/Loader';
import React from 'react';
import { Link } from 'react-router-dom';
import theme from 'theme';
import H1SemiBold from 'theme/components/Typography/H1SemiBold';
import H2SemiBold from 'theme/components/Typography/H2SemiBold';
import P2Medium from 'theme/components/Typography/P2Medium';
import P2Regular from 'theme/components/Typography/P2Regular';

export interface PinCodePresenterProps {
  backPath: string;
  backLabel: string;
  loading: boolean;
  generating: boolean;
  pinCode: string | null;
  remainingChanges: number | null;
  showPin: boolean;
  temporaryPinCodesPath: string;
  onGeneratePin: () => void;
  onShowPinStart: () => void;
  onShowPinEnd: () => void;
  _t: (msgid: string) => string;
}

/**
 * Returns a normalized list of PIN characters for presentation.
 */
function getPinDigits(pinCode: string | null): string[] {
  if (!pinCode) return ['*', '*', '*', '*', '*'];

  const digits = pinCode.split('');
  while (digits.length < 5) digits.push('*');

  return digits.slice(0, 5);
}

/**
 * Renders the permanent PIN page UI independently of the backing data source.
 */
export default function PinCodePresenter({
  backPath,
  backLabel,
  loading,
  generating,
  pinCode,
  remainingChanges,
  showPin,
  temporaryPinCodesPath,
  onGeneratePin,
  onShowPinStart,
  onShowPinEnd,
  _t,
}: PinCodePresenterProps) {
  const pinDigits = getPinDigits(pinCode);

  return (
    <Box p="1rem">
      <Box mb="1rem">
        <Flex>
          <Link
            to={backPath}
            style={{ textDecoration: 'none', display: 'flex', alignContent: 'center', marginBottom: '0.5rem' }}
          >
            <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
            <P2Medium color={theme.colors?.gray?.[9]}>{backLabel}</P2Medium>
          </Link>
        </Flex>
        <Flex gap="0.5rem" align="center">
          <IconCodeAsterix size="2rem" color={theme.colors?.gray?.[12]} />
          <H1SemiBold color={theme.colors?.gray?.[12]}>{_t('PIN code')}</H1SemiBold>
        </Flex>
      </Box>
      {loading ? (
        <Loader />
      ) : (
        <Stack gap="1rem" pb="5rem">
          <Box
            style={{
              backgroundColor: 'white',
              borderRadius: '0.625rem',
              padding: '0.875rem',
            }}
          >
            <Flex justify="flex-end" mb="1rem">
              <Button
                variant="transparent"
                size="sm"
                leftSection={<IconRefresh size="1.25rem" />}
                onClick={onGeneratePin}
                loading={generating}
                disabled={remainingChanges === 0}
                style={{
                  padding: 0,
                  color: remainingChanges === 0 ? theme.colors?.gray?.[5] : theme.colors?.primary?.[0],
                  backgroundColor: 'transparent',
                }}
              >
                {_t('Generate new PIN code')}
              </Button>
            </Flex>

            <Flex justify="center" gap="0.5rem" mb="1.5rem">
              {pinDigits.map((digit, index) => (
                <Box
                  key={index}
                  style={{
                    width: '3.5rem',
                    height: '5.625rem',
                    border: `1px dashed ${theme.colors?.gray?.[6]}`,
                    borderRadius: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    color: theme.colors?.gray?.[12],
                  }}
                >
                  {showPin ? digit : <IconAsterisk size="1.5rem" color={theme.colors?.gray?.[9]} />}
                </Box>
              ))}
            </Flex>

            <Button
              fullWidth
              size="md"
              rightSection={<IconEye size="1.5rem" />}
              onMouseDown={onShowPinStart}
              onMouseUp={onShowPinEnd}
              onMouseLeave={onShowPinEnd}
              onTouchStart={onShowPinStart}
              onTouchEnd={onShowPinEnd}
              style={{ userSelect: 'none' }}
            >
              {_t('Show PIN code')}
            </Button>

            {remainingChanges !== null && (
              <P2Medium color={theme.colors?.gray?.[6]} mt="1rem" style={{ textAlign: 'center' }}>
                {_t('Remaining changes')}: {remainingChanges}
              </P2Medium>
            )}

            {remainingChanges === 0 && (
              <P2Medium color={theme.colors?.gray?.[12]} mt="0.5rem" style={{ textAlign: 'center' }}>
                {_t('You have reached the limit of PIN changes for today.')}
              </P2Medium>
            )}
          </Box>

          <Box
            style={{
              backgroundColor: 'white',
              borderRadius: '0.625rem',
              padding: '0.875rem',
            }}
          >
            <Stack gap="0.75rem">
              <Box pr="2rem">
                <H2SemiBold color={theme.colors?.gray?.[12]}>{_t('Temporary PIN codes')}</H2SemiBold>
                <P2Regular color={theme.colors?.gray?.[6]}>
                  {_t('Create up to 5 temporary 6-digit PIN codes with a fixed start and end time.')}
                </P2Regular>
              </Box>
              <Link to={temporaryPinCodesPath} style={{ textDecoration: 'none' }}>
                <Button fullWidth>{_t('Temporary entries')}</Button>
              </Link>
            </Stack>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
